/**
 * Sale effects v2's switch in v1's sale listener (docs/architecture/SALE_EFFECTS.md §3; DISCOUNT_V2_PLAN.md §9.10,
 * decisions 5, 32 and 33; E3). Both platforms' `setupSaleUpdateListener` ask `runPromotionEffectsV2` first, for
 * every saved sale:
 *
 * - a write that told its intent (a v2 surface's, through `repriceSale`'s `intent`) is v2's, whatever the gate says;
 * - an untold write (v1's own code) is v2's when sale effects v2's gate is on for the terminal;
 * - otherwise v1's listener runs all of its own effects, as before.
 *
 * When the write is v2's, v2 runs the four promotion effects (the release on a discard, the points refund and the
 * partner release on a bill change, the customer's loyalty fetch) and v1's listener runs only the rest (the cart
 * events and the recommendations, until E2). Never both, per effect (decision 32).
 *
 * The one place v1's listener reaches v2. Deleted with v1's listener at cutover.
 */
import type { ApiClient } from "../../../api/types";
import { apiClient } from "../../../api/registry";
import { selectPartnerDirectory, selectSaleData } from "../../../discounts/selectors";
import { isOffline } from "../../../facade/ServiceFacade";
import { fetchDetailedCustomerInfo } from "../../../helpers/customerHelpers";
import { selectCustomerLoyaltyInfo, selectCustomerLoyaltyRefId } from "../../../loyalty/selectors";
import { repriceSale } from "../../../ordering/writePath";
import { createPartnerPort } from "../../../promotions/partners/port";
import { toldIntent, type SaleWriteIntent } from "../../../saleEffects/changes";
import { saleEffectsV2Enabled } from "../../../saleEffects/config";
import { createSaleEffectsExecutor, type SaleEffectsExecutor } from "../../../saleEffects/executor";
import { toasts } from "../../../toast";
import store from "../../../redux-store";

const call: ApiClient["call"] = (route, input) => apiClient().call(route, input);

let executor: SaleEffectsExecutor | null = null;

/** The app's one executor: its in-flight guards must outlive a single save. Made on first use, never at load. */
function appExecutor(): SaleEffectsExecutor {
  executor ??= createSaleEffectsExecutor({
    port: createPartnerPort({ call }),
    directory: () => selectPartnerDirectory(store.getState()),
    currentSale: () => selectSaleData(store.getState()),
    // its own writes are v2's too, so v1's effects never run on what v2's just wrote
    write: (sale) => void repriceSale(sale, { intent: {} }),
    isOnline: () => !isOffline(),
    toast: toasts,
    loadCustomerRecord: (customerRefId) => fetchDetailedCustomerInfo(customerRefId),
    isCustomerRecordLoaded: (customerRefId) => {
      const state = store.getState();
      return !!selectCustomerLoyaltyInfo(state) && selectCustomerLoyaltyRefId(state) === customerRefId;
    },
  });
  return executor;
}

/**
 * Hand this saved sale's promotion effects to sale effects v2 when the write is v2's (see the header). `untoldIntent`
 * is what the platform reads for a write that told nothing: web reads v1's markers (decision 33), mobile has none. It
 * is called before any of v1's effects run, so a marker v1's cart event clears is read first.
 *
 * Returns whether v2 took them: the caller then runs only the effects v2 has not taken over.
 */
export function runPromotionEffectsV2(action: unknown, listenerApi: any, untoldIntent: () => SaleWriteIntent): boolean {
  const told = toldIntent(action);
  const state = listenerApi.getState();
  if (!told && !saleEffectsV2Enabled(state?.businessStore?.businessData)) return false;
  const previous = listenerApi.getOriginalState()?.saleDataStore?.saleData;
  const next = state?.saleDataStore?.saleData;
  void appExecutor().run(previous, next, told ?? untoldIntent());
  return true;
}
